import type { LazyImageProps } from './LazyImage.types'
import cn from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isSrcInvalid } from '../../utils/images/isSrcInvalid'
import { BaseImage } from '../image/BaseImage'
import styles from './LazyImage.module.css'

export function LazyImage({ alt, placeholderSrc, srcSet, sizes, className, ...props }: LazyImageProps) {
  const [imageError, setImageError] = useState(false)
  const [placeholderError, setPlaceholderError] = useState(false)
  const [imageIsLoaded, setImageIsLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)

  const { src } = props

  useEffect(() => {
    setImageError(false)
    setPlaceholderError(false)
    setImageIsLoaded(false)
  }, [src, placeholderSrc])

  /**
   * Whenever an image is cached after a subsequent request, browser
   * is asking for image before component is rehydrated and load\error
   * event is being fired without being capture by `onLoadCapture`\
   * `onErrorCapture`, making the placeholder always visible in this
   * scenario. This check prevents that by checking image ref on component
   * mount.
   */
  const checkIfImageWasLoadedBeforeHydration = useCallback(() => {
    const wasLoaded = imgRef.current?.complete
    if (wasLoaded) {
      setImageIsLoaded(true)
    }

    if (wasLoaded && imgRef.current?.naturalWidth === 0) {
      setImageError(true)
    }
  }, [imgRef])

  useEffect(() => {
    checkIfImageWasLoadedBeforeHydration()
  })

  if (isSrcInvalid(src) || imageError) {
    return (
      <BaseImage isEmpty alt={alt} className={className} {...props} />
    )
  }

  return (
    <div className={cn(styles.imageWrapper, className)}>
      {!imageIsLoaded && !placeholderError && (
        <BaseImage
          alt={alt}
          {...props}
          className={cn(styles.placeholder)}
          src={placeholderSrc}
          loading="eager"
          onErrorCapture={() => {
            setPlaceholderError(true)
          }}
        />
      )}
      <BaseImage
        ref={imgRef}
        alt={alt}
        {...props}
        srcSet={srcSet}
        sizes={sizes}
        loading="lazy"
        className={cn(styles.image, {
          [styles.imageLoaded]: imageIsLoaded,
          [styles.imageLoading]: !imageIsLoaded,
        })}
        onErrorCapture={() => setImageError(true)}
        onLoadCapture={() => setImageIsLoaded(true)}
      />
    </div>
  )
}
